import React, { useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { usePriceSubscription } from '../../hooks/usePriceSubscription';
import { CheckIcon, CloseIcon } from '../../helpers/Icons';
import {
  getOrderOpenPrice,
  isBuyOrder,
  isPendingOrder,
  modifyOrder,
} from '../../services/platform/TradingStore';
import { getTicketValue } from './portfolioUtils';
import NumberInput from './NumberInput';

const ModifyOrderModal = ({ order, onClose }) => {
  const theme = useTheme();
  const currentPrice = usePriceSubscription(order.symbol);

  const [sl, setSL] = useState(order.priceSL ?? order.sl ?? '');
  const [tp, setTP] = useState(order.priceTP ?? order.tp ?? '');
  const [expire, setExpire] = useState(order.expire || '');
  const pending = isPendingOrder(order);
  const orderPrice = getOrderOpenPrice(order);
  const [price, setPrice] = useState(orderPrice > 0 ? orderPrice : '');

  const digits = order.digits ?? 5;
  const point = Math.pow(10, -digits);
  const pip = point * 10;
  const minGapPips = 40;

  const initializeField = (field) => {
    if (!currentPrice) return;
    const bid = currentPrice.bid;
    const ask = currentPrice.ask;
    const offset = (minGapPips + 10) * pip;

    if (field === 'price' && pending) {
      setPrice(currentPrice.bid.toFixed(digits));
      return;
    }

    if (field === 'sl') {
      const basePrice = parseFloat(price) || (isBuyOrder(order) ? ask : bid);
      const val = pending
        ? (isBuyOrder(order) ? basePrice - offset : basePrice + offset)
        : (isBuyOrder(order) ? bid - offset : ask + offset);
      setSL(val.toFixed(digits));
      return;
    }

    if (field === 'tp') {
      const basePrice = parseFloat(price) || (isBuyOrder(order) ? ask : bid);
      const val = pending
        ? (isBuyOrder(order) ? basePrice + offset : basePrice - offset)
        : (isBuyOrder(order) ? ask + offset : bid - offset);
      setTP(val.toFixed(digits));
    }
  };

  const adjValue = (val, setter, delta, field) => {
    let current = parseFloat(val);
    if (isNaN(current)) {
      if (!currentPrice) return;
      const bid = currentPrice.bid;
      const ask = currentPrice.ask;
      const offset = (minGapPips + 10) * pip;
      if (field === 'price') current = bid;
      else if (field === 'sl') current = isBuyOrder(order) ? bid - offset : ask + offset;
      else if (field === 'tp') current = isBuyOrder(order) ? ask + offset : bid - offset;
      else current = bid;
    } else {
      current += delta;
    }
    setter(current.toFixed(digits));
  };

  const handleSave = () => {
    modifyOrder(order, {
      sl,
      tp,
      price: pending ? price : undefined,
      expire: pending ? expire : undefined,
    });
    onClose();
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: theme.card, borderRadius: 12, width: '100%', maxWidth: 340, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', border: `1px solid ${theme.border}` }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>Modify Order #{getTicketValue(order)}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><CloseIcon s={14} c={theme.textM} /></button>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pending && (
            <NumberInput
              label="Price"
              val={price}
              onChange={setPrice}
              placeholder="0.00000"
              color={theme.primary + '44'}
              onStep={(d) => adjValue(price, setPrice, d, 'price')}
              field="price"
              theme={theme}
              point={point}
              onInitialize={initializeField}
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <NumberInput
              label="Stop Loss"
              val={sl}
              onChange={setSL}
              placeholder="0.00000"
              color={theme.neg + '33'}
              onStep={(d) => adjValue(sl, setSL, d, 'sl')}
              field="sl"
              theme={theme}
              point={point}
              onInitialize={initializeField}
            />
            <NumberInput
              label="Take Profit"
              val={tp}
              onChange={setTP}
              placeholder="0.00000"
              color={theme.pos + '33'}
              onStep={(d) => adjValue(tp, setTP, d, 'tp')}
              field="tp"
              theme={theme}
              point={point}
              onInitialize={initializeField}
            />
          </div>

          {pending && (
            <NumberInput label="Expiry (Optional)" val={expire} onChange={setExpire} type="datetime-local" color={theme.border} theme={theme} point={0} onInitialize={null} />
          )}

          <button onClick={handleSave} style={{ width: '100%', background: theme.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <CheckIcon s={14} c="#fff" /> Update Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModifyOrderModal;
